<template>
  <b-card v-if="render">
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="notification-title"
            class="mb-2"
          >
            <b-form-input
              id="notification-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="notification-lang"
            class="mb-2"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('notifications.linkTitle')"
            label-for="notification-linkTitle"
            class="mb-2"
          >
            <b-form-input
              id="notification-linkTitle"
              v-model="form.linkTitle"
              :state="validateState('linkTitle')"
            />
            <b-form-invalid
              id="input-linkTitle-feedback"
              :label="$t('notifications.linkTitle')"
              :validator="$v.form.linkTitle"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('notifications.link')"
            label-for="notification-link"
            class="mb-2"
          >
            <b-form-input
              id="notification-link"
              v-model="form.link"
              :state="validateState('link')"
            />
            <b-form-invalid
              id="input-link-feedback"
              :label="$t('notifications.link')"
              :validator="$v.form.link"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.type')"
            label-for="notification-add-type"
            class="mb-2"
            :state="validateState('type')"
          >
            <v-select-custom
              id="notification-add-type"
              v-model="form.type"
              :options="typeOptions"
            />
            <b-form-invalid
              id="input-type-feedback"
              :label="$t('general.type')"
              :validator="$v.form.type"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('notifications.isActive')"
            label-for="notification-isActive"
            class="mb-2"
          >
            <b-form-checkbox
              id="notification-isActive"
              v-model="form.isActive"
              class="mt-1"
              name="notification-isActive"
              switch
              inline
            />
            <b-form-invalid
              id="input-isActive-feedback"
              :label="$t('general.isActive')"
              :validator="$v.form.isActive"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="notification-description"
            class="mb-2"
          >
            <b-form-textarea
              id="notification-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="notification-image"
            v-model="form.notificationImage"
            :label="$t('general.image') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.selectFile')"
            :state="validateState('notificationImage')"
            :alt.sync="form.notificationImageAlt"
            :alt-validator="$v.form.notificationImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-notification-image-feedback"
                :label="$t('general.image')"
                :validator="$v.form.notificationImage"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BCard,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BFormTextarea,
  BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { required, minLength } from 'vuelidate/lib/validators'

import languageSelector from '@/components/language-selector.vue'
import { isLink } from '@core/utils/rules'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import BFileInput from '@/components/BFileInput/index.vue'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import useNotificationsList from './useNotificationsList'

export default {
  name: 'NotificationEdit',
  components: {
    BFileInput,
    BFormInvalid,
    BFormTextarea,
    BCard,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    languageSelector,
    LayoutLoading,
    VSelectCustom,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: { required, minLength: minLength(3) },
        description: { required, minLength: minLength(10) },
        notificationImage: { required },
        notificationImageAlt: { required, minLength: minLength(3) },
        type: { required },
        link: { required, isLink },
        linkTitle: { required, minLength: minLength(3) },
        isActive: { required },
      },
    }
  },
  setup() {
    const {
      convertResource,
      currentLanguage,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      typeOptions,
      updateItem,
    } = useNotificationsList()

    return {
      convertResource,
      currentLanguage,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      typeOptions,
      updateItem,
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  created() {
    this.form.lang = 'fa'
    this.fetchItem(this.itemId)
  },
  methods: {
    changeSelectedLanguage(val) {
      this.form = this.convertResource(this.item, val)
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0]
          .scrollIntoView({ behavior: 'smooth', block: 'center' }), 500)
        return
      }
      const uploadList = ['notificationImage']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        description: form.description || '',
        notificationImageFileId: form.notificationImage ? form.notificationImage._id : null,
        type: form.type ? form.type._id : '',
        link: form.link || '',
        linkTitle: form.linkTitle || '',
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
